/**
 * Keeping the browser's session alive: `POST /v1/auth/refresh/`.
 *
 * Called when the app starts and whenever the tab regains focus. It sends
 * nothing until the token is past HALF its 30-day life (`tokenTiming.ts`), so
 * on an ordinary day this is a base64 decode and a comparison.
 *
 * ── The rules that keep it safe ──────────────────────────────────────
 *  - SINGLE FLIGHT. Focus and visibility events arrive together; two
 *    concurrent refreshes would each mint a session and revoke the other's.
 *  - Only the SERVER says a session is dead. A 401 from the refresh endpoint
 *    (`dead`) is the one answer that ends the sign-in; a token that merely
 *    looks expired by this clock is asked about, never condemned.
 *  - A failed REFRESH is not a failed SESSION. Offline, a timeout or a 5xx
 *    changes nothing about the account and backs off (30 s, 2 min, 10 min,
 *    30 min) so a focus event does not retry on every tab switch.
 *  - 403 `refresh_unavailable` means a DEVICE-LINK token - the one a browser
 *    signed in by QR holds. It exists so one browser can be signed out on its
 *    own, and a refresh would mint a token that no longer names the device.
 *    Such a session is renewed by linking again, so this does nothing, and
 *    stops asking for that token. (A 404, a server without the endpoint, is
 *    the same.)
 *  - The answer is dropped if the session changed while the request was in
 *    flight (signed out, or signed in as somebody else): writing a token for
 *    one account over another is the one way this could do harm.
 *
 * The session lives in `localStorage`, shared by every tab, so a token one
 * tab refreshes is the token the others read next (session.ts
 * `onSessionChange` keeps their copy current); the old one is ended a few
 * minutes later (`ROTATION_GRACE_SECONDS` server-side), which covers a
 * request already in flight in another tab.
 */
import { api, ApiError } from './api';
import { getSession, setSession } from './session';
import { backoffMs, classifyRefreshStatus, refreshWanted } from './tokenTiming';

export type RefreshOutcome =
  /** Nothing to do: signed out, still fresh, or a token that cannot refresh. */
  | 'skipped'
  | 'refreshed'
  /** The server says this session is over (401). The caller signs out. */
  | 'dead'
  /** 403 / 404: this token is never refreshed. */
  | 'unavailable'
  /** Could not reach the server; the next try is after a back-off. */
  | 'network'
  /** Inside a back-off window. */
  | 'quiet';

const REFRESH_TIMEOUT_MS = 15_000;

let inFlight: Promise<RefreshOutcome> | null = null;
let failures = 0;
let quietUntil = 0;
/** The one token the server has told us it will not refresh. In memory only. */
let unavailableToken: string | null = null;

/** Test-only: forget everything this module has learned. */
export function __resetRefreshForTests(): void {
  inFlight = null;
  failures = 0;
  quietUntil = 0;
  unavailableToken = null;
}

async function run(startedWith: string, now: number): Promise<RefreshOutcome> {
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), REFRESH_TIMEOUT_MS);
  try {
    const res = await api<{ token?: string }>('/v1/auth/refresh/', {
      method: 'POST',
      body: {},
      signal: ac.signal,
    });
    const current = getSession();
    // Signed out, or somebody else signed in, while this was in flight.
    if (!current || current.token !== startedWith) return 'skipped';
    if (typeof res?.token !== 'string' || !res.token) return 'network';
    setSession({ ...current, token: res.token });
    failures = 0;
    quietUntil = 0;
    return 'refreshed';
  } catch (err) {
    const status = err instanceof ApiError ? err.status : 0;
    switch (classifyRefreshStatus(status)) {
      case 'unauthorized':
        // A 401 for a token that is no longer the session's (somebody signed
        // in while this was in flight) says nothing about the one that is.
        return getSession()?.token === startedWith ? 'dead' : 'skipped';
      case 'unavailable':
        unavailableToken = startedWith;
        return 'unavailable';
      default:
        failures += 1;
        quietUntil = now + backoffMs(failures);
        return 'network';
    }
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Refresh the signed-in token if it is past half its life.
 *
 * Never throws. On `'refreshed'` the new token is already in the session
 * store - every request and socket reads it from there - and the caller may
 * re-read `getSession()` to bring React state up to date.
 */
export function refreshIfDue(now: number = Date.now()): Promise<RefreshOutcome> {
  const session = getSession();
  if (!session) return Promise.resolve('skipped');
  if (session.token === unavailableToken) return Promise.resolve('skipped');
  if (!refreshWanted(session.token, now)) return Promise.resolve('skipped');
  if (inFlight) return inFlight;
  if (now < quietUntil) return Promise.resolve('quiet');

  inFlight = run(session.token, now).finally(() => {
    inFlight = null;
  });
  return inFlight;
}
